import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/tr';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { theme } from './theme/theme';
import { AppRouter } from './app/AppRouter';

/** Every `DatePicker` in the app (`@mui/x-date-pickers`' `AdapterDayjs`) renders/parses
 *  its text field using dayjs's locale-specific `L` format token — dayjs's own `tr`
 *  locale defines that as `DD.MM.YYYY` (dot-separated, the traditional Turkish
 *  convention), not the slash-separated `DD/MM/YYYY` this app wants everywhere. A single
 *  `updateLocale` override here (before anything renders) fixes every `DatePicker`
 *  project-wide — no need to pass a `format` prop to each individual instance. Also
 *  switches the active dayjs locale from the default `en` (`MM/DD/YYYY`) to `tr`, which
 *  incidentally fixes the day/month names shown in every picker's calendar popup too. */
dayjs.extend(updateLocale);
dayjs.updateLocale('tr', { formats: { L: 'DD/MM/YYYY' } });
dayjs.locale('tr');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
    mutations: { retry: false },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
            <AppRouter />
          </LocalizationProvider>
        </QueryClientProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
